// SW-29: general feedback (everyone) + the admin surface (/v1/admin/*).
// Shapes follow agents/specs/SW-29-ADMIN-AND-FEEDBACK-CONTRACT-2026-09-11.md.
// Admin rows are rendered generically (whatever columns the API sends), so
// only the fields the UI reads by name are typed. Nothing here is cached:
// every screen show is a fresh fetch.
import { apiFetch } from "./client";
import { isLoggedIn } from "./auth";

export type Row = Record<string, unknown>;

/** POST /v1/support/feedback -- auth optional (the header rides along only
 * when a token is stored); `contact_email` is null when signed in or blank. */
export function sendFeedback(message: string, contactEmail: string | null): Promise<void> {
  return apiFetch<void>("/v1/support/feedback", {
    method: "POST",
    body: { message, client: "web", contact_email: contactEmail },
    auth: true,
  });
}

export interface AdminAccount extends Row {
  id: number;
  email: string;
}
export interface AdminAccountDetail {
  account: AdminAccount;
  subscriptions: Row[];
  devices: Row[];
  alerts: Row[];
  bug_reports: Row[];
  feedback: Row[];
  audit: Row[];
}
export interface AdminStats extends Row {
  new_users_by_day: Row[];
  errors_by_day: Row[];
  active_subscriptions_by_platform: Row;
  cancellation_reasons: Row[];
  funnel: Row;
}
export type AdminAction = "grant" | "revoke" | "revoke-tokens" | "send-reset" | "delete";
export type AdminListKind = "bug-reports" | "feedback" | "errors" | "audit";

export function getAdminStats(days = 7): Promise<AdminStats> {
  return apiFetch<AdminStats>(`/v1/admin/stats?days=${days}`, { auth: true });
}
export function listAdminAccounts(q: string): Promise<{ accounts: AdminAccount[] }> {
  return apiFetch(`/v1/admin/accounts?q=${encodeURIComponent(q)}`, { auth: true });
}
export function getAdminAccount(id: number): Promise<AdminAccountDetail> {
  return apiFetch<AdminAccountDetail>(`/v1/admin/accounts/${id}`, { auth: true });
}
export function adminAccountAction(id: number, action: AdminAction, body?: unknown): Promise<void> {
  if (action === "delete") {
    return apiFetch<void>(`/v1/admin/accounts/${id}`, { method: "DELETE", auth: true });
  }
  return apiFetch<void>(`/v1/admin/accounts/${id}/${action}`, { method: "POST", body, auth: true });
}
export function listAdminItems(kind: AdminListKind): Promise<{ items: Row[] }> {
  return apiFetch(`/v1/admin/${kind}`, { auth: true });
}

/** MENU gate: ADMIN shows only when the signed-in account gets a 200 from
 * GET /v1/admin/stats?days=1. Anonymous never asks; any failure = not admin. */
export async function isAdminAccount(): Promise<boolean> {
  if (!isLoggedIn()) return false;
  try {
    await getAdminStats(1);
    return true;
  } catch {
    return false;
  }
}
